import { inventoryIdentifier } from "./inventoryItemFields";

// Use lifecycle status, never the independent assignment flag or translated label.
export const isInventoryActionBlocked = (item) => {
  const status = String(item?.raw?.status ?? item?.statusCode ?? item?.status ?? "").trim().toUpperCase();
  return status === "RETURNED" || status === "RETURN" || status === "REMOVED" || status === "INSTALLED";
};

export const isInventoryReassignAllowed = (item) => {
  const status = String(item?.raw?.status ?? item?.statusCode ?? item?.status ?? "").trim().toUpperCase();
  return status === "ASSIGNED" || status === "REASSIGNED" || status === "AVAILABLE";
};

// Status and assignment are separate API values. An explicit assignment flag wins.
export const getAssignmentCode = (item) => {
  if (typeof item.assigned === "boolean") return item.assigned ? "ASSIGNED" : "AVAILABLE";
  return item.assignedTo || item.groupId || item.groupAssignment || item.status === "ASSIGNED" || item.status === "REASSIGNED"
    ? "ASSIGNED"
    : "AVAILABLE";
};

export const getGroupAssignmentOutcome = (response, requestedIds, requestedItems = []) => {
  const value = response?.body?.value ?? response?.body ?? response;
  const results = Array.isArray(value?.results) ? value.results : [];
  const failures = [value?.failures, value?.errors, value?.failedItems, value?.rejected]
    .find(Array.isArray) || [];
  const resultStatus = (result) => String(result?.status ?? "").toUpperCase();
  const failed = (result) =>
    result?.assigned === false ||
    result?.transferred === false ||
    result?.success === false ||
    Boolean(result?.reason) ||
    Boolean(result?.error) ||
    resultStatus(result) === "FAILED";
  const succeeded = (result) =>
    !failed(result) &&
    (result?.assigned === true ||
      result?.transferred === true ||
      result?.success === true ||
      ["SUCCESS", "ASSIGNED", "REASSIGNED", "COMPLETED", "TRANSFERRED"].includes(resultStatus(result)));
  const successfulResults = results.filter(succeeded);
  const failedResults = results.filter(failed);
  const count = (number) => (Number.isFinite(Number(number)) ? Math.max(0, Number(number)) : 0);
  const failedCount = Math.max(count(value?.failedCount), failedResults.length, failures.length);
  const assignedCount = Math.max(
    count(value?.assignedCount ?? value?.transferredCount ?? value?.successCount),
    successfulResults.length
  );
  const matchesItem = (result, id) => {
    if (result?.inventoryItemId != null) return String(result.inventoryItemId) === String(id);
    const item = requestedItems.find((entry) => String(entry.id) === String(id));
    const number = inventoryIdentifier(result);
    const requestedNumber = inventoryIdentifier(item?.raw) ?? inventoryIdentifier(item);
    return number != null && requestedNumber != null && String(number).trim() === String(requestedNumber).trim();
  };
  // Keep unidentified failures selected rather than silently removing them.
  const failedIds = requestedIds.filter(
    (id) =>
      failedResults.concat(failures).some((result) => matchesItem(result, id)) ||
      !successfulResults.some((result) => matchesItem(result, id))
  );
  const reason =
    failedResults
      .concat(failures)
      .concat(results.filter((r) => r?.reason || r?.error || r?.message))
      .map((result) => (typeof result === "string" ? result : result?.reason || result?.message || result?.error))
      .find((message) => typeof message === "string" && message.trim()) ||
    (typeof value?.error === "string" ? value.error : value?.error?.message || value?.message);
  const status = String(value?.status ?? "").toUpperCase();
  const pending =
    ["PENDING", "QUEUED", "ACCEPTED", "PROCESSING", "IN_PROGRESS"].includes(status) ||
    (!status &&
      (value?.inventoryUploadId != null || value?.uploadId != null) &&
      !results.length &&
      value?.assignedCount == null &&
      value?.transferredCount == null &&
      value?.successCount == null &&
      !failedCount);
  return { failedCount, assignedCount, failedIds, reason, pending };
};
